import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Zap,
  Plus,
  Play,
  Save,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Activity,
  Workflow,
  GitBranch,
  Mail,
  Clock,
  CheckSquare,
  UserCheck,
  UserPlus,
  Briefcase,
  CheckCircle,
  ArrowRightLeft,
  TrendingUp,
  DollarSign,
  Search,
  AlertTriangle,
  Target,
  User,
  Eye,
  Circle,
} from 'lucide-react';
import { ComingSoonBanner } from '../ComingSoonBanner';
import NodeEditModal from './components/NodeEditModal';
import SaveWorkflowModal from './components/SaveWorkflowModal';
import WorkflowTestModal from './components/WorkflowTestModal';

interface ProcessTemplate {
  id: string;
  name: string;
  category: string;
  description: string;
  icon: string;
  nodes: WorkflowNode[];
  connections: WorkflowConnection[];
  status: string;
  lastModified: string;
}

interface WorkflowComponent {
  id: string;
  name: string;
  icon: React.ReactNode;
  category: string;
}

interface WorkflowNode {
  id: string;
  type: string;
  label: string;
  x?: number;
  y?: number;
  assignee?: string;
  approver?: string;
}

interface WorkflowConnection {
  from: string;
  to: string;
  condition?: string;
}

interface ActiveWorkflow {
  id: string;
  processName: string;
  employee: string;
  status: string;
  currentStep: string;
  progress: number;
  startedAt: string;
  stepsCompleted: number;
  totalSteps: number;
  delayReason?: string;
}

// Process templates with full workflow data
const processTemplates: ProcessTemplate[] = [
  {
    id: 'onboard-1',
    name: 'New Employee Onboarding',
    category: 'onboarding',
    description: 'Complete onboarding workflow with document collection, system access, and training',
    icon: 'UserPlus',
    nodes: [
      { id: 'node-1', type: 'start', label: 'New Hire Confirmed', x: 100, y: 100 },
      { id: 'node-2', type: 'task', label: 'Send Welcome Email', x: 100, y: 200, assignee: 'HR Team' },
      { id: 'node-3', type: 'task', label: 'Collect Documents', x: 100, y: 300, assignee: 'HR Coordinator' },
      { id: 'node-4', type: 'approval', label: 'Manager Approval', x: 100, y: 400, approver: 'Direct Manager' },
      { id: 'node-5', type: 'task', label: 'Setup System Access', x: 100, y: 500, assignee: 'IT Team' },
      { id: 'node-6', type: 'end', label: 'Onboarding Complete', x: 100, y: 600 }
    ],
    connections: [
      { from: 'node-1', to: 'node-2' },
      { from: 'node-2', to: 'node-3' },
      { from: 'node-3', to: 'node-4' },
      { from: 'node-4', to: 'node-5', condition: 'approved' },
      { from: 'node-5', to: 'node-6' }
    ],
    status: 'active',
    lastModified: '2024-01-10'
  },
  {
    id: 'approval-1',
    name: 'Multi-Level Leave Approval',
    category: 'approvals',
    description: 'Cascading approval workflow for extended leave requests',
    icon: 'CheckCircle',
    nodes: [
      { id: 'node-1', type: 'start', label: 'Leave Request Submitted', x: 100, y: 100 },
      { id: 'node-2', type: 'decision', label: 'Duration > 5 days?', x: 100, y: 200 },
      { id: 'node-3', type: 'approval', label: 'Manager Approval', x: 50, y: 300, approver: 'Direct Manager' },
      { id: 'node-4', type: 'approval', label: 'Department Head Approval', x: 200, y: 300, approver: 'Department Head' },
      { id: 'node-5', type: 'approval', label: 'HR Approval', x: 200, y: 400, approver: 'HR Manager' },
      { id: 'node-6', type: 'end', label: 'Leave Approved', x: 100, y: 500 }
    ],
    connections: [
      { from: 'node-1', to: 'node-2' },
      { from: 'node-2', to: 'node-3', condition: 'no' },
      { from: 'node-2', to: 'node-4', condition: 'yes' },
      { from: 'node-3', to: 'node-6' },
      { from: 'node-4', to: 'node-5' },
      { from: 'node-5', to: 'node-6' }
    ],
    status: 'active',
    lastModified: '2024-01-12'
  },
  {
    id: 'approval-2',
    name: 'Expense Approval Cascade',
    category: 'approvals',
    description: 'Amount-based routing for expense approvals',
    icon: 'DollarSign',
    nodes: [
      { id: 'node-1', type: 'start', label: 'Expense Submitted', x: 100, y: 100 },
      { id: 'node-2', type: 'decision', label: 'Amount > $1000?', x: 100, y: 200 },
      { id: 'node-3', type: 'approval', label: 'Manager Approval', x: 50, y: 300, approver: 'Direct Manager' },
      { id: 'node-4', type: 'approval', label: 'Finance Approval', x: 200, y: 300, approver: 'Finance Manager' },
      { id: 'node-5', type: 'task', label: 'Process Reimbursement', x: 100, y: 400, assignee: 'Finance Team' },
      { id: 'node-6', type: 'end', label: 'Reimbursement Complete', x: 100, y: 500 }
    ],
    connections: [
      { from: 'node-1', to: 'node-2' },
      { from: 'node-2', to: 'node-3', condition: 'no' },
      { from: 'node-2', to: 'node-4', condition: 'yes' },
      { from: 'node-3', to: 'node-5' },
      { from: 'node-4', to: 'node-5' },
      { from: 'node-5', to: 'node-6' }
    ],
    status: 'active',
    lastModified: '2024-01-11'
  },
  {
    id: 'role-1',
    name: 'Promotion Workflow',
    category: 'roleTransitions',
    description: 'Role transition for employee promotions with approval and system updates',
    icon: 'TrendingUp',
    nodes: [
      { id: 'node-1', type: 'start', label: 'Promotion Initiated', x: 100, y: 100 },
      { id: 'node-2', type: 'approval', label: 'Manager Approval', x: 100, y: 200, approver: 'Direct Manager' },
      { id: 'node-3', type: 'approval', label: 'HR Approval', x: 100, y: 300, approver: 'HR Director' },
      { id: 'node-4', type: 'task', label: 'Update Job Title', x: 100, y: 400, assignee: 'HR Team' },
      { id: 'node-5', type: 'task', label: 'Adjust Compensation', x: 100, y: 500, assignee: 'Payroll Team' },
      { id: 'node-6', type: 'end', label: 'Promotion Complete', x: 100, y: 600 }
    ],
    connections: [
      { from: 'node-1', to: 'node-2' },
      { from: 'node-2', to: 'node-3' },
      { from: 'node-3', to: 'node-4' },
      { from: 'node-4', to: 'node-5' },
      { from: 'node-5', to: 'node-6' }
    ],
    status: 'active',
    lastModified: '2024-01-09'
  },
  {
    id: 'role-2',
    name: 'Department Transfer',
    category: 'roleTransitions',
    description: 'Workflow for transferring employees between departments',
    icon: 'ArrowRightLeft',
    nodes: [
      { id: 'node-1', type: 'start', label: 'Transfer Request', x: 100, y: 100 },
      { id: 'node-2', type: 'approval', label: 'Current Manager Approval', x: 100, y: 200 },
      { id: 'node-3', type: 'approval', label: 'New Manager Approval', x: 100, y: 300 },
      { id: 'node-4', type: 'task', label: 'Update Department', x: 100, y: 400, assignee: 'HR Team' },
      { id: 'node-5', type: 'end', label: 'Transfer Complete', x: 100, y: 500 }
    ],
    connections: [
      { from: 'node-1', to: 'node-2' },
      { from: 'node-2', to: 'node-3' },
      { from: 'node-3', to: 'node-4' },
      { from: 'node-4', to: 'node-5' }
    ],
    status: 'draft',
    lastModified: '2024-01-07'
  }
];

const componentPalette: WorkflowComponent[] = [
  { id: 'trigger', name: 'Trigger', icon: <Zap className="w-4 h-4" />, category: 'Start' },
  { id: 'approval', name: 'Approval Step', icon: <UserCheck className="w-4 h-4" />, category: 'Actions' },
  { id: 'email', name: 'Send Email', icon: <Mail className="w-4 h-4" />, category: 'Actions' },
  { id: 'delay', name: 'Wait/Delay', icon: <Clock className="w-4 h-4" />, category: 'Flow' },
  { id: 'condition', name: 'Condition', icon: <GitBranch className="w-4 h-4" />, category: 'Flow' },
  { id: 'task', name: 'Create Task', icon: <CheckSquare className="w-4 h-4" />, category: 'Actions' },
];

const activeWorkflows: ActiveWorkflow[] = [
  {
    id: 'wf-001',
    processName: 'New Employee Onboarding',
    employee: 'Sarah Johnson',
    status: 'in-progress',
    currentStep: 'Manager Approval',
    progress: 60,
    startedAt: '2024-01-14 09:00',
    stepsCompleted: 3,
    totalSteps: 5
  },
  {
    id: 'wf-002',
    processName: 'Multi-Level Leave Approval',
    employee: 'Michael Chen',
    status: 'pending',
    currentStep: 'Department Head Approval',
    progress: 40,
    startedAt: '2024-01-14 10:30',
    stepsCompleted: 2,
    totalSteps: 5
  },
  {
    id: 'wf-003',
    processName: 'Promotion Workflow',
    employee: 'Emma Davis',
    status: 'at-risk',
    currentStep: 'HR Approval',
    progress: 75,
    startedAt: '2024-01-13 14:00',
    stepsCompleted: 5,
    totalSteps: 7,
    delayReason: 'Approval overdue by 4 hours'
  },
  {
    id: 'wf-004',
    processName: 'Department Transfer',
    employee: 'James Wilson',
    status: 'completed',
    currentStep: 'Transfer Complete',
    progress: 100,
    startedAt: '2024-01-12 09:00',
    stepsCompleted: 6,
    totalSteps: 6
  }
];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active':
    case 'in-progress':
      return 'bg-green-100 text-green-800';
    case 'draft':
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'at-risk':
      return 'bg-red-100 text-red-800';
    case 'completed':
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getNodeTypeColor = (type: string) => {
  switch (type) {
    case 'start':
      return 'bg-green-500 border-green-600';
    case 'end':
      return 'bg-red-500 border-red-600';
    case 'task':
      return 'bg-blue-500 border-blue-600';
    case 'approval':
      return 'bg-purple-500 border-purple-600';
    case 'decision':
      return 'bg-amber-500 border-amber-600';
    default:
      return 'bg-gray-500 border-gray-600';
  }
};

const getIconComponent = (iconName: string) => {
  const icons: Record<string, React.ReactNode> = {
    UserPlus: <UserPlus className="w-4 h-4" />,
    CheckCircle: <CheckCircle className="w-4 h-4" />,
    DollarSign: <DollarSign className="w-4 h-4" />,
    TrendingUp: <TrendingUp className="w-4 h-4" />,
    ArrowRightLeft: <ArrowRightLeft className="w-4 h-4" />,
    Briefcase: <Briefcase className="w-4 h-4" />,
  };
  return icons[iconName] || <Workflow className="w-4 h-4" />;
};

export default function HRProcessAutomation() {
  const { t } = useTranslation();
  const [activeView, setActiveView] = useState<'builder' | 'monitoring'>('builder');
  const [isLibraryCollapsed, setIsLibraryCollapsed] = useState(false);
  const [isPaletteCollapsed, setIsPaletteCollapsed] = useState(false);
  const [showNodeEditModal, setShowNodeEditModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [selectedNode, setSelectedNode] = useState<WorkflowNode | null>(null);
  const [selectedProcess, setSelectedProcess] = useState<ProcessTemplate | null>(null);
  const [workflowNodes, setWorkflowNodes] = useState<WorkflowNode[]>([]);
  const [workflowConnections, setWorkflowConnections] = useState<WorkflowConnection[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({
    onboarding: true,
    approvals: true,
    roleTransitions: true,
  });
  const [statusFilter, setStatusFilter] = useState('all');

  const categoryLabels: Record<string, string> = {
    onboarding: 'Onboarding Workflows',
    approvals: 'Approval Cascades',
    roleTransitions: 'Role Transitions',
  };

  const handleLoadProcess = (process: ProcessTemplate) => {
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Do you want to discard them?')) {
        return;
      }
    }
    setSelectedProcess(process);
    setWorkflowNodes(process.nodes);
    setWorkflowConnections(process.connections);
    setHasUnsavedChanges(false);
  };

  const handleNewWorkflow = () => {
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Do you want to discard them?')) {
        return;
      }
    }
    setSelectedProcess(null);
    setWorkflowNodes([]);
    setWorkflowConnections([]);
    setHasUnsavedChanges(false);
  };

  const handleAddNode = (component: WorkflowComponent) => {
    const newNode: WorkflowNode = {
      id: `node-${Date.now()}`,
      type: component.id,
      label: component.name,
      x: 100,
      y: workflowNodes.length * 100 + 100
    };
    setWorkflowNodes([...workflowNodes, newNode]);
    setHasUnsavedChanges(true);
  };

  const handleNodeClick = (node: WorkflowNode) => {
    setSelectedNode(node);
    setShowNodeEditModal(true);
  };

  const handleSaveNode = (node: WorkflowNode) => {
    setWorkflowNodes(prev => prev.map(n => n.id === node.id ? node : n));
    setShowNodeEditModal(false);
    setHasUnsavedChanges(true);
  };

  const handleSaveWorkflow = () => {
    console.log('Workflow saved successfully');
    setShowSaveModal(false);
    setHasUnsavedChanges(false);
  };

  const filteredProcesses = processTemplates.filter(process =>
    process.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    process.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredWorkflows = statusFilter === 'all'
    ? activeWorkflows
    : activeWorkflows.filter(wf => wf.status === statusFilter);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category as keyof typeof prev]
    }));
  };

  const getCategoryProcesses = (category: string) => {
    return filteredProcesses.filter(p => p.category === category);
  };

  const performanceMetrics = [
    { label: 'Active Workflows', value: '12', change: '+3', trend: 'up', icon: <Activity className="w-5 h-5" />, color: 'text-blue-600' },
    { label: 'Avg. Completion Time', value: '2.3 days', change: '-0.5 days', trend: 'down', icon: <Clock className="w-5 h-5" />, color: 'text-green-600' },
    { label: 'Success Rate', value: '94%', change: '+2%', trend: 'up', icon: <CheckCircle className="w-5 h-5" />, color: 'text-green-600' },
    { label: 'Bottlenecks', value: '2', change: '-1', trend: 'down', icon: <AlertTriangle className="w-5 h-5" />, color: 'text-amber-600' },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <ComingSoonBanner
        icon={<Zap className="w-6 h-6" />}
        title="HR Process Automation"
        description="HR Process Automation is currently in development. This is a preview of the upcoming feature."
        gradientFrom="from-rose-500"
        gradientTo="to-pink-500"
      />

      {/* Header */}
      <div className="bg-card rounded-2xl shadow-sm border border-border p-8 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('HR Process Automation Center')}</h1>
              <p className="text-gray-600">{t('Design and manage automated HR workflows')}</p>
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-border">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setActiveView('builder')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeView === 'builder'
                  ? 'bg-rose-500 text-white shadow-sm'
                  : 'bg-card border border-border text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Workflow className="w-4 h-4" />
              {t('Workflow Builder')}
            </button>
            <button
              onClick={() => setActiveView('monitoring')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeView === 'monitoring'
                  ? 'bg-rose-500 text-white shadow-sm'
                  : 'bg-card border border-border text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Activity className="w-4 h-4" />
              {t('Process Monitoring')}
            </button>
          </div>

          {activeView === 'builder' && (
            <div className="flex items-center gap-2">
              {hasUnsavedChanges && (
                <span className="text-xs text-amber-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {t('Unsaved changes')}
                </span>
              )}
              <button
                className="flex items-center gap-2 px-3 py-1.5 border border-border text-gray-700 rounded-lg text-sm hover:bg-gray-50"
                onClick={handleNewWorkflow}
              >
                <Plus className="w-4 h-4" />
                {t('New Workflow')}
              </button>
              <button
                className="flex items-center gap-2 px-3 py-1.5 border border-border text-gray-700 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
                onClick={() => setShowTestModal(true)}
                disabled={workflowNodes.length === 0}
              >
                <Play className="w-4 h-4" />
                {t('Test')}
              </button>
              <button
                className="flex items-center gap-2 px-3 py-1.5 bg-rose-500 text-white rounded-lg text-sm hover:bg-rose-600 disabled:opacity-50"
                onClick={() => setShowSaveModal(true)}
                disabled={workflowNodes.length === 0}
              >
                <Save className="w-4 h-4" />
                {t('Save Workflow')}
              </button>
            </div>
          )}
        </div>
      </div>

      {activeView === 'builder' ? (
        /* Builder View */
        <div className="grid grid-cols-12 gap-6 mb-6">
          {/* Left Panel - Process Library */}
          <div className={`${isLibraryCollapsed ? 'col-span-1' : 'col-span-3'} transition-all`}>
            <div className="bg-card rounded-lg shadow-sm border border-border h-[500px] flex flex-col">
              <div className="flex items-center justify-between p-4 border-b border-border">
                {!isLibraryCollapsed && (
                  <h3 className="font-semibold text-gray-900">{t('Process Library')}</h3>
                )}
                <button
                  onClick={() => setIsLibraryCollapsed(!isLibraryCollapsed)}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                >
                  {isLibraryCollapsed ? (
                    <ChevronRight className="w-4 h-4 text-gray-600" />
                  ) : (
                    <ChevronLeft className="w-4 h-4 text-gray-600" />
                  )}
                </button>
              </div>
              {!isLibraryCollapsed && (
                <div className="flex-1 overflow-y-auto">
                  {/* Search */}
                  <div className="p-4 border-b border-border">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder={t('Search processes...')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500"
                      />
                    </div>
                  </div>

                  {/* Process Categories */}
                  <div className="p-4 space-y-4">
                    {Object.keys(categoryLabels).map((category) => {
                      const processes = getCategoryProcesses(category);
                      if (processes.length === 0) return null;

                      return (
                        <div key={category} className="space-y-2">
                          <button
                            onClick={() => toggleCategory(category)}
                            className="w-full flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">
                                {t(categoryLabels[category])}
                              </span>
                              <span className="text-xs text-gray-500">({processes.length})</span>
                            </div>
                            {expandedCategories[category as keyof typeof expandedCategories] ? (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                          </button>

                          {expandedCategories[category as keyof typeof expandedCategories] && (
                            <div className="space-y-2 ml-2">
                              {processes.map((process) => (
                                <button
                                  key={process.id}
                                  onClick={() => handleLoadProcess(process)}
                                  className={`w-full text-left p-3 rounded-lg border transition-all ${
                                    selectedProcess?.id === process.id
                                      ? 'bg-rose-50 border-rose-300 shadow-sm'
                                      : 'bg-card border-border hover:border-foreground/20 hover:shadow-sm'
                                  }`}
                                >
                                  <div className="flex items-start gap-3">
                                    <div className={`p-2 rounded-lg ${
                                      selectedProcess?.id === process.id ? 'bg-rose-100' : 'bg-gray-100'
                                    }`}>
                                      {getIconComponent(process.icon)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h4 className="text-sm font-medium text-gray-900 mb-1">
                                        {process.name}
                                      </h4>
                                      <div className="flex items-center gap-2 mt-1">
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${getStatusColor(process.status)}`}>
                                          <Circle className="w-1.5 h-1.5 fill-current" />
                                          {process.status}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          {process.nodes.length} {t('steps')}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Center Panel - Workflow Canvas */}
          <div className={`${isLibraryCollapsed && isPaletteCollapsed ? 'col-span-10' : isLibraryCollapsed || isPaletteCollapsed ? 'col-span-8' : 'col-span-6'} transition-all`}>
            <div className="bg-card rounded-lg shadow-sm border border-border h-[500px] overflow-auto">
              {workflowNodes.length === 0 ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-rose-100 to-pink-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Workflow className="w-8 h-8 text-rose-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('Workflow Canvas')}</h3>
                    <p className="text-gray-500 text-sm max-w-sm mx-auto">
                      {t('Select a process from the library or add components from the palette to build your automation.')}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="p-6">
                  {selectedProcess && (
                    <div className="mb-4 p-3 bg-rose-50 rounded-lg border border-rose-200">
                      <h3 className="font-medium text-rose-900">{selectedProcess.name}</h3>
                      <p className="text-sm text-rose-700">{selectedProcess.description}</p>
                    </div>
                  )}
                  <div className="space-y-4">
                    {workflowNodes.map((node, index) => (
                      <div key={node.id} className="flex items-center gap-4">
                        <div
                          onClick={() => handleNodeClick(node)}
                          className={`flex-1 p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${getNodeTypeColor(node.type)} text-white`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{node.label}</span>
                            <span className="text-xs opacity-75 capitalize">{node.type}</span>
                          </div>
                          {node.assignee && (
                            <div className="text-xs mt-1 opacity-75">{t('Assignee')}: {node.assignee}</div>
                          )}
                          {node.approver && (
                            <div className="text-xs mt-1 opacity-75">{t('Approver')}: {node.approver}</div>
                          )}
                        </div>
                        {index < workflowNodes.length - 1 && (
                          <div className="text-gray-400">→</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Component Palette */}
          <div className={`${isPaletteCollapsed ? 'col-span-1' : 'col-span-3'} transition-all`}>
            <div className="bg-card rounded-lg shadow-sm border border-border h-[500px]">
              <div className="flex items-center justify-between p-4 border-b border-border">
                <button
                  onClick={() => setIsPaletteCollapsed(!isPaletteCollapsed)}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                >
                  {isPaletteCollapsed ? (
                    <ChevronLeft className="w-4 h-4 text-gray-600" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-600" />
                  )}
                </button>
                {!isPaletteCollapsed && (
                  <h3 className="font-semibold text-gray-900">{t('Components')}</h3>
                )}
              </div>
              {!isPaletteCollapsed && (
                <div className="p-4 space-y-2">
                  {componentPalette.map((component) => (
                    <div
                      key={component.id}
                      onClick={() => handleAddNode(component)}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-rose-50 hover:border-rose-200 border border-transparent transition-colors"
                    >
                      <div className="w-8 h-8 bg-rose-100 rounded-lg flex items-center justify-center text-rose-600">
                        {component.icon}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{t(component.name)}</p>
                        <p className="text-xs text-gray-500">{t(component.category)}</p>
                      </div>
                      <Plus className="w-4 h-4 text-gray-400 ml-auto" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* Monitoring View */
        <div className="space-y-6">
          {/* Header with Filters */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{t('Process Monitoring')}</h2>
              <p className="text-sm text-gray-600">{t('Track active workflows and identify bottlenecks')}</p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-border rounded-lg text-sm"
              >
                <option value="all">{t('All Status')}</option>
                <option value="in-progress">{t('In Progress')}</option>
                <option value="pending">{t('Pending')}</option>
                <option value="at-risk">{t('At Risk')}</option>
                <option value="completed">{t('Completed')}</option>
              </select>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {performanceMetrics.map((metric, index) => (
              <div key={index} className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className={metric.color}>{metric.icon}</span>
                  <span className={`text-xs font-medium ${
                    metric.trend === 'up' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {metric.change}
                  </span>
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-1">{metric.value}</div>
                <div className="text-sm text-gray-600">{t(metric.label)}</div>
              </div>
            ))}
          </div>

          {/* Active Workflows */}
          <div className="bg-card border border-border rounded-lg">
            <div className="p-4 border-b border-border">
              <h3 className="font-semibold text-gray-900">{t('Active Workflows')}</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {filteredWorkflows.map((workflow) => (
                <div key={workflow.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-medium text-gray-900">{workflow.processName}</h4>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(workflow.status)}`}>
                          {workflow.status.replace('-', ' ')}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5" />
                          {workflow.employee}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {t('Started')} {workflow.startedAt}
                        </span>
                        <span className="flex items-center gap-1">
                          <Target className="w-3.5 h-3.5" />
                          {workflow.stepsCompleted}/{workflow.totalSteps} {t('steps')}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="flex items-center gap-2 px-3 py-1.5 border border-border rounded-lg text-sm text-gray-700 hover:bg-gray-50">
                        <Eye className="w-4 h-4" />
                        {t('View')}
                      </button>
                      {workflow.status === 'at-risk' && (
                        <button className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-sm hover:bg-amber-200">
                          <AlertTriangle className="w-4 h-4" />
                          {t('Intervene')}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-2">
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                      <span>{t('Current')}: {workflow.currentStep}</span>
                      <span>{workflow.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          workflow.status === 'at-risk' ? 'bg-red-500'
                            : workflow.status === 'completed' ? 'bg-green-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${workflow.progress}%` }}
                      />
                    </div>
                  </div>

                  {workflow.delayReason && (
                    <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                      <AlertCircle className="w-3 h-3" />
                      {workflow.delayReason}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showNodeEditModal && selectedNode && (
        <NodeEditModal
          node={selectedNode}
          onSave={handleSaveNode}
          onClose={() => setShowNodeEditModal(false)}
        />
      )}
      {showSaveModal && (
        <SaveWorkflowModal
          workflow={{ nodes: workflowNodes, connections: workflowConnections }}
          existingProcess={selectedProcess}
          onClose={() => setShowSaveModal(false)}
          onSave={handleSaveWorkflow}
        />
      )}
      {showTestModal && (
        <WorkflowTestModal
          workflow={{ nodes: workflowNodes, connections: workflowConnections }}
          onClose={() => setShowTestModal(false)}
        />
      )}
    </div>
  );
}
